import React, { useState, useEffect } from 'react'
import Api from '../../../../_helpers/Api'
import { trans } from '../../../../_providers/Translation'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    IconButton,
    Grid,
    Box,
    Typography,
    CircularProgress,
    TablePagination
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import DateRange from '@/components/partials/DateRange'
import moment from 'moment'
import Pagination from '@/components/Pagination'
import Skeleton from 'react-loading-skeleton'
import NoDataFound from '@/components/partials/NoDataFound'

// Styles converted from makeStyles to sx prop objects
const styles = {
    root: {
        width: '100%',
        p: 3,
    },
    pageTitle: {
        mb: 3,
    },
    filtersContainer: {
        backgroundColor: '#f8f9fa',
        p: 2.5,
        borderRadius: 1,
        mb: 3,
    },
    searchField: {
        minWidth: 200,
        backgroundColor: 'white',
    },
    selectField: {
        minWidth: 200,
        backgroundColor: 'white',
    },
    dateField: {
        maxWidth: 150,
        backgroundColor: 'white',
    },
    clearButton: {
        textTransform: 'none',
    },
    tableContainer: {
        borderRadius: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    tableHeader: {
        backgroundColor: '#f5f5f5',
        fontWeight: 600,
    },
    noData: {
        textAlign: 'center',
        p: 5,
        color: '#999',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 5,
    },
}

function LoginHistory(props) {
    const [state, setState] = useState({
        loading: true,
        data: [],
        filteredData: [],
        searchTerm: '',
        selectedEmployee: '',
        startDate: null,
        endDate: null,
        employees: [],
        rowsPerPage: 10,
        total: 0,
        pages: 0,
        page: 1,
    })

    // Load employees once on mount
    useEffect(() => {
        loadEmployees()
    }, [props.id])

    // Load login history when page or rowsPerPage changes
    useEffect(() => {
        loadLoginHistory()
    }, [state.page, state.rowsPerPage])

    // Reset to first page when filters change (but don't trigger if already on page 0)
    useEffect(() => {
        if (state.page !== 0) {
            setState(prev => ({ ...prev, page: 0 }))
        } else {
            // If already on page 0, just reload the data
            loadLoginHistory()
        }
    }, [state.searchTerm, state.selectedEmployee, state.startDate, state.endDate])

    const loadLoginHistory = () => {
        setState(prev => ({
            ...prev,
            loading: true
        }))

        const page = state.page // Backend expects 1-indexed pages
        const limit = state.rowsPerPage

        // Build query parameters
        let queryParams = `partner_id=${props.id}&page=${page}&limit=${limit}`

        // Add search filter
        if (state.searchTerm) {
            queryParams += `&search=${encodeURIComponent(state.searchTerm)}`
        }

        // Add employee filter
        if (state.selectedEmployee) {
            queryParams += `&employee_id=${state.selectedEmployee}`
        }

        // Add date range filter
        if (state.startDate) {
            const startDateStr = moment(state.startDate).format('YYYY-MM-DD') // Format: YYYY-MM-DD
            queryParams += `&start_date=${startDateStr}`
        }

        if (state.endDate) {
            const endDateStr = moment(state.endDate).format('YYYY-MM-DD') // Format: YYYY-MM-DD
            queryParams += `&end_date=${endDateStr}`
        }

        Api.get(`partners/login-history?${queryParams}`)
            .then(res => {
                // Backend returns: { total, pages, items }
                const loginData = res.data?.items || []
                const totalCount = res.data?.pages || 0

                setState(prev => ({
                    ...prev,
                    loading: false,
                    data: loginData,
                    filteredData: loginData,
                    total: totalCount
                }))
            })
            .catch(err => {
                console.error('Error loading login history:', err)
                setState(prev => ({
                    ...prev,
                    loading: false,
                    data: [],
                    filteredData: [],
                    total: 0
                }))
            })
    }

    const loadEmployees = () => {
        Api.get(`partners/employees/all?partner_id=${props.id}`)
            .then(res => {
                // Ensure employees is always an array
                const employeesData = Array.isArray(res.data) ? res.data : (res.data?.data || [])
                setState(prev => ({
                    ...prev,
                    employees: employeesData
                }))
            })
            .catch(err => {
                console.error('Error loading employees:', err)
                setState(prev => ({
                    ...prev,
                    employees: []
                }))
            })
    }

    const handleSearch = (value) => {
        setState(prev => ({
            ...prev,
            searchTerm: value
        }))
    }

    const handleEmployeeFilter = (value) => {
        setState(prev => ({
            ...prev,
            selectedEmployee: value
        }))
    }

    const handleDateFilter = (name, date) => {
        setState(prev => ({
            ...prev,
            [name]: date
        }))
    }

    const clearFilters = () => {
        setState(prev => ({
            ...prev,
            searchTerm: '',
            selectedEmployee: '',
            startDate: null,
            endDate: null,
            page: 0
        }))
    }

    const handleChangePage = (page) => {
        setState(prev => ({
            ...prev,
            page: page.selected + 1
        }))
    }

    const handleChangeRowsPerPage = (event) => {
        setState(prev => ({
            ...prev,
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }))
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString)
        const day = String(date.getDate()).padStart(2, '0')
        const month = date.toLocaleString('bg-BG', { month: 'short' })
        const year = date.getFullYear()
        const time = date.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' })
        return `${day} ${month} ${year} / ${time}`
    }

    return (
        <div className="pannel">
            <h3>
                {trans('headings.loginHistory') || 'История на влизанията'}
            </h3>

            <div className='filter-standard'>
                <div className='col'>
                    <input type="search"
                        placeholder={trans('filters.search') || 'Търсене...'}
                        value={state.searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </div>
                <div className='col'>
                    <select
                        value={state.selectedEmployee}
                        onChange={(e) => handleEmployeeFilter(e.target.value)}
                    >
                        <option value="">{trans('labels.allEmployees') || 'Всички служители'}</option>
                        {state.employees.map(emp => (
                            <option key={emp.id} value={emp.id}>{emp.name}</option>
                        ))}
                    </select>
                </div>
                <div className='col'>
                    <DateRange
                        startDate={state.startDate}
                        endDate={state.endDate}
                        onStartDateChange={(date) => {
                            handleDateFilter('startDate', date)
                        }} onEndDateChange={(date) => {
                            handleDateFilter('endDate', date)
                        }}
                    />
                </div>
                <div className='col'>
                    <Button
                        onClick={clearFilters}
                        className='button-clear-filter'
                    >
                    </Button>
                </div>
            </div>

            <hr />

            {/* Table */}
            {state.loading ? (
                <Skeleton count={10} height={45} />
            ) : (
                state.filteredData.length === 0 ?
                    <NoDataFound />
                    :
                    <>
                        <div className="table-scroller">
                            <table>
                                <thead>
                                    <tr>
                                        <th>
                                            {trans('labels.id') || 'ID'}
                                        </th>
                                        <th>
                                            {trans('labels.date') || 'Дата'}
                                        </th>
                                        <th>
                                            {trans('labels.employee') || 'Служител'}
                                        </th>
                                        <th>
                                            {trans('labels.device') || 'Устройство'}
                                        </th>
                                        <th>
                                            {trans('labels.ipAddress') || 'IP Адрес'}
                                        </th>
                                        {/* <th>
                                            {trans('labels.status') || 'Статус'}
                                        </th>
                                        <th>
                                            {trans('labels.failureReason') || 'Причина за неуспех'}
                                        </th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {state.filteredData.map((item, index) => (
                                        <tr key={item.id || index} hover>
                                            <td>{item.id}</td>
                                            <td>
                                                {item.logged_in_at ? formatDate(item.logged_in_at) : (item.created_at ? formatDate(item.created_at) : '-')}
                                            </td>
                                            <td>{item.employee?.full_name || '-'}</td>
                                            <td>{item.device || '-'}</td>
                                            <td>{item.ip_address || '-'}</td>
                                            {/* <td>
                                                {item.successful ? (
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            color: '#4caf50',
                                                            fontWeight: 600,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            padding: '4px 12px',
                                                            backgroundColor: '#e8f5e9',
                                                            borderRadius: '12px'
                                                        }}
                                                    >
                                                        ✓ {trans('labels.successful')}
                                                    </Typography>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            color: '#f44336',
                                                            fontWeight: 600,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            padding: '4px 12px',
                                                            backgroundColor: '#ffebee',
                                                            borderRadius: '12px'
                                                        }}
                                                    >
                                                        ✗ {trans('labels.failed') || 'Неуспешен'}
                                                    </Typography>
                                                )}
                                            </td>
                                            <td>
                                                {item.failure_reason ? (
                                                    <Typography variant="body2" color="error">
                                                        {item.failure_reason}
                                                    </Typography>
                                                ) : '-'}
                                            </td> */}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            pages={state.total}
                            page={state.page || 1}
                            handlePage={handleChangePage}
                        />
                    </>
            )}
        </div>
    )
}

export default LoginHistory
